import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPhone,
  faEnvelope,
  faEye,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import Loader from "../../components/utils/Loader";
import Tooltip from "../../components/utils/Tooltip";
import { jsPDF } from "jspdf";

const Consultants = () => {
  const location = useLocation();
  const authState = useSelector((state) => state.authReducer);
  const [userId] = useState(authState.user?._id);

  const isFoundHerContext =
    location.pathname.includes("/foundher") ||
    location.pathname.includes("think5whys-counsellors/foundher");

  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchFirm, setSearchFirm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [imageErrors, setImageErrors] = useState(new Set());
  const [selectedConsultantId, setSelectedConsultantId] = useState(null);

  const [fetchData] = useFetch();

  const fetchConsultants = useCallback(async () => {
    setLoading(true);
    try {
      const config = {
        url: "/consultants/list",
        method: "post",
        headers: { Authorization: authState.token },
        data: {
          type: 1,
          context: isFoundHerContext ? "foundher" : undefined,
        },
      };
      const response = await fetchData(config, { showSuccessToast: false });
      if (response && Array.isArray(response.consultants)) {
        setConsultants(response.consultants);
      }
    } catch (error) {
      console.error("Error fetching consultants:", error);
    } finally {
      setLoading(false);
    }
  }, [authState.token, fetchData, isFoundHerContext]);

  useEffect(() => {
    if (authState.isLoggedIn) {
      fetchConsultants();
    }
  }, [authState.isLoggedIn, fetchConsultants]);

  // Filter for FoundHer context
  let displayedConsultants = consultants;
  if (isFoundHerContext) {
    displayedConsultants = consultants.filter(
      (c) => c.isAvailableFree === true,
    );
  }

  const filteredConsultants = [...displayedConsultants]
    .filter(
      (consultant) =>
        consultant.name?.toLowerCase().includes(searchName.toLowerCase()) &&
        (!searchCity ||
          consultant.city?.toLowerCase().includes(searchCity.toLowerCase())) &&
        (!searchFirm ||
          (consultant.firmName || consultant.businessName)
            ?.toLowerCase()
            .includes(searchFirm.toLowerCase())),
    )
    .sort((a, b) => {
      const A = a[sortField] || "";
      const B = b[sortField] || "";
      return sortOrder === "asc"
        ? String(A).localeCompare(String(B))
        : String(B).localeCompare(String(A));
    });

  const handleCallClick = (contactNumber) =>
    (window.location.href = `tel:${contactNumber}`);
  const handleWhatsAppClick = (contactNumber) =>
    window.open(`https://wa.me/${contactNumber}`, "_blank");
  const handleEmailClick = (contactEmail) =>
    (window.location.href = `mailto:${contactEmail}`);

  const generateConsultantPDF = (consultant) => {
    const pdf = new jsPDF({ unit: "pt" });
    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(14);
    pdf.text("Consultant Details", 40, 40);
    pdf.setFontSize(11);

    let y = 70;
    const addLine = (label, value) => {
      pdf.text(`${label}: ${value || "N/A"}`, 40, y);
      y += 20;
    };

    addLine("Name", consultant.name);
    addLine("Business Name", consultant.firmName || consultant.businessName);
    addLine("City", consultant.city);
    addLine("Country", consultant.country);
    addLine("Website", consultant.website);
    addLine("Personal Counsellor", consultant.isCounsellor ? "Yes" : "No");
    addLine("Company Consultant", consultant.isConsultant ? "Yes" : "No");
    addLine("Brief Bio", consultant.briefBio);
    addLine("Work Status", consultant.workStatus);
    addLine("Charges", `${consultant.charges} ${consultant.chargesCurrency}`);

    y += 20;
    pdf.setFontSize(13);
    pdf.text("Reviews", 40, y);
    y += 20;
    pdf.setFontSize(11);

    if (consultant.reviews && consultant.reviews.length > 0) {
      consultant.reviews.forEach((review, index) => {
        pdf.text(
          `${index + 1}. ${review.reviewerName} | ${new Date(review.reviewDate).toLocaleDateString()}`,
          40,
          y,
        );
        y += 18;
        if (y > 750) {
          pdf.addPage();
          y = 40;
        }
      });
    } else {
      pdf.text("No reviews available.", 40, y);
    }

    pdf.save(`${consultant.name}-profile.pdf`);
  };

  return (
    <div className="bg-zinc-950 text-gray-50 min-h-screen pb-12">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-zinc-900 border-b border-yellow-500/30 px-4 py-5 shadow-md">
        <h1 className="text-2xl font-bold text-yellow-200 text-center tracking-wide">
          Counsellors and Consultants @ Think5Whys
        </h1>
      </header>

      <div className="max-w-7xl mx-auto px-4 pt-6">
        {/* FoundHer Banner */}
        {isFoundHerContext && (
          <div className="mb-8 bg-emerald-900/60 border border-emerald-500/50 rounded-2xl p-5 text-center">
            <p className="text-emerald-300 text-lg font-medium">
              💚 Support for Young Women Entrepreneurs on FoundHer
            </p>
            <p className="text-emerald-400 mt-2">
              Prices are not applicable. These consultants are freely supporting
              women founders.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-zinc-900 rounded-3xl px-6 py-2 mb-4 border border-zinc-700">
          <div className="flex flex-wrap gap-2 mb-2">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-full px-4 py-1 bg-zinc-800 border border-zinc-700 rounded-2xl focus:border-yellow-400 focus:outline-none text-sm placeholder:text-gray-200"
            />
            <input
              type="text"
              placeholder="Search by city..."
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              className="w-full px-4 py-1 bg-zinc-800 border border-zinc-700 rounded-2xl focus:border-yellow-400 focus:outline-none text-sm placeholder:text-gray-200"
            />
            <input
              type="text"
              placeholder="Search by firm..."
              value={searchFirm}
              onChange={(e) => setSearchFirm(e.target.value)}
              className="w-full px-4 py-1 bg-zinc-800 border border-zinc-700 rounded-2xl focus:border-yellow-400 focus:outline-none text-sm placeholder:text-gray-200"
            />
          </div>

          <div className="flex flex-row gap-2 items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="text-gray-100 text-sm">Sort By:</label>
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-xl w-fit px-2 py-1 text-sm"
              >
                <option value="name">Name</option>
                <option value="city">City</option>
                <option value="firmName">Business Name</option>
                <option value="workStatus">Work Status</option>
              </select>
            </div>

            <button
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="w-fit px-2 py-1 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-2xl flex items-center gap-2 transition"
            >
              {sortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
            </button>
          </div>
          <div>
            <Link
              to="/consultants/add"
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-zinc-950 font-semibold w-fit px-2 py-1 rounded-2xl transition ml-auto"
            >
              <FontAwesomeIcon icon={faPlus} />
              Register as Consultant / Counsellor
            </Link>
          </div>
        </div>

        {/* Results Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-white">
            {filteredConsultants.length} Consultant
            {filteredConsultants.length !== 1 ? "s" : ""} Found
            {isFoundHerContext && " (Free Support)"}
          </h3>
        </div>

        {/* Consultants Grid */}
        {loading ? (
          <Loader />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredConsultants.length === 0 ? (
              <div className="col-span-full text-center py-20">
                <p className="text-2xl text-yellow-300">
                  No matching records found
                </p>
              </div>
            ) : (
              filteredConsultants.map((consultant) => (
                <div
                  key={consultant._id}
                  className="bg-zinc-900 border border-zinc-700 rounded-3xl overflow-hidden hover:border-yellow-400/60 transition-all duration-300 flex flex-col"
                >
                  {/* Avatar */}
                  <div className="relative h-56 bg-zinc-800 flex items-center justify-center overflow-hidden">
                    {imageErrors.has(consultant._id) ? (
                      <span className="text-7xl">👩‍💼</span>
                    ) : (
                      <img
                        src={`/api/downloads/consultant/${consultant.userCode}.png`}
                        alt={consultant.name}
                        className="w-full h-full object-cover"
                        onError={() =>
                          setImageErrors(
                            (prev) => new Set([...prev, consultant._id]),
                          )
                        }
                      />
                    )}

                    {consultant.isAvailableFree && (
                      <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                        FREE FOR FOUNDHER
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-semibold text-white mb-1">
                      {consultant.name}
                    </h3>
                    <p className="text-emerald-400 mb-4">
                      {consultant.firmName || consultant.businessName}
                    </p>

                    <div className="grid grid-cols-3 gap-3 mb-6">
                      <button
                        onClick={() => handleCallClick(consultant.mobile)}
                        className="flex flex-col items-center gap-1 bg-zinc-800 hover:bg-zinc-700 w-fit px-2 py-1 rounded-2xl transition"
                      >
                        <FontAwesomeIcon icon={faPhone} className="text-lg" />
                        <span className="text-xs">Call</span>
                      </button>
                      <button
                        onClick={() => handleWhatsAppClick(consultant.mobile)}
                        className="flex flex-col items-center gap-1 bg-zinc-800 hover:bg-zinc-700 w-fit px-2 py-1 rounded-2xl transition"
                      >
                        <FontAwesomeIcon
                          icon={faWhatsapp}
                          className="text-lg"
                        />
                        <span className="text-xs">WhatsApp</span>
                      </button>
                      <button
                        onClick={() => handleEmailClick(consultant.email)}
                        className="flex flex-col items-center gap-1 bg-zinc-800 hover:bg-zinc-700 w-fit px-2 py-1 rounded-2xl transition"
                      >
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="text-lg"
                        />
                        <span className="text-xs">Email</span>
                      </button>
                    </div>

                    <button
                      onClick={() =>
                        setSelectedConsultantId(
                          selectedConsultantId === consultant._id
                            ? null
                            : consultant._id,
                        )
                      }
                      className="mt-auto w-fit px-2 py-1 border border-cyan-400 text-cyan-300 hover:bg-cyan-950 rounded-2xl flex items-center justify-center gap-2 font-medium transition"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      {selectedConsultantId === consultant._id
                        ? "Hide Details"
                        : "View Full Details"}
                    </button>

                    {/* Expanded Details */}
                    {selectedConsultantId === consultant._id && (
                      <div className="mt-8 pt-6 border-t border-zinc-700 text-sm space-y-4">
                        <p className="text-gray-100">
                          <span className="text-gray-100">City:</span>{" "}
                          {consultant.city}
                        </p>
                        <p className="text-gray-100">
                          <span className="text-gray-100">Country:</span>{" "}
                          {consultant.country}
                        </p>
                        <p className="text-gray-100">
                          <span className="text-gray-100">Website:</span>{" "}
                          {consultant.website}
                        </p>
                        <p className="text-gray-100">
                          <span className="text-gray-100">Counsellor:</span>{" "}
                          {consultant.isCounsellor ? "Yes" : "No"}
                        </p>
                        <p className="text-gray-100">
                          <span className="text-gray-100">Consultant:</span>{" "}
                          {consultant.isConsultant ? "Yes" : "No"}
                        </p>
                        <p className="text-gray-100">
                          <span className="text-gray-100">Work Status:</span>{" "}
                          {consultant.workStatus}
                        </p>
                        <p className="text-gray-100">
                          <span className="text-gray-100">Charges:</span>{" "}
                          {consultant.charges} {consultant.chargesCurrency}
                        </p>
                        <p className="pt-4 border-t text-gray-100 border-zinc-700">
                          <span className="text-gray-100">Bio:</span>{" "}
                          {consultant.briefBio}
                        </p>

                        <div className="pt-4">
                          <h4 className="text-cyan-300 font-medium mb-3">
                            Reviews
                          </h4>
                          {consultant.reviews?.length > 0 ? (
                            consultant.reviews.map((review, i) => (
                              <div
                                key={i}
                                className="bg-zinc-800 p-4 rounded-2xl mb-3"
                              >
                                <p className="text-gray-100">
                                  {review.reviewerName} •{" "}
                                  {new Date(
                                    review.reviewDate,
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-100">No reviews yet.</p>
                          )}
                        </div>

                        <Tooltip text="Download PDF Profile" position="top">
                          <button
                            onClick={() => generateConsultantPDF(consultant)}
                            className="w-fit px-2 py-1 mt-4 flex items-center justify-center gap-2 text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 rounded-2xl"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                            Download PDF
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Consultants;
